import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event), null);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back to the parent screen. Animates the closing screen's items out and the
   * returning screen's items back in via the Web Animations API (independent of any
   * CSS cascade — sidesteps quirks where the entrance animation rule is "stuck" on
   * the same elements).
   * @param {Event} [event]
   */
  back(event) {
    const closingDetails = this.#getDetailsElement(event);
    const isTopLevel = closingDetails === this.refs.details;
    const returningDetails = isTopLevel ? null : closingDetails.parentElement?.closest('details');
    this.#close(closingDetails, returningDetails ?? null);
  }

  /**
   * Close the entire drawer.
   */
  close() {
    this.#close(this.refs.details, null);
  }

  /**
   * Close the closest menu or submenu that is open.
   *
   * @param {HTMLDetailsElement} details - The details being closed.
   * @param {HTMLDetailsElement | null} returningDetails - The parent details whose
   *   items should re-animate in once the close completes. Null for full-drawer close.
   */
  async #close(details, returningDetails) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');

    // Hide the returning screen's items NOW so they don't flash visible when the
    // closing panel fades out and reveals what's behind it. The Phase 3 animation
    // will animate them back in from this hidden state.
    if (returningDetails) {
      prehideItemsForReturn(returningDetails);
    }

    // Phase 1: animate items out via Web Animations API while the panel is still in
    // its `.menu-open` state. The panel doesn't move yet so the per-item stagger is
    // fully visible — no fighting with CSS transitions on the parent.
    const exitResult = animateScreenItems(details, EXIT_KEYFRAMES, EXIT_TIMING);
    await exitResult.finished;

    // Phase 2: remove .menu-open so the panel slides/fades out via its CSS transition.
    details.classList.remove('menu-open');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        // Release the forwards-held exit state so reopening starts from a clean slate.
        exitResult.animations.forEach((a) => a.cancel());

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }

        // Phase 3: re-animate the returning screen's items so the previous level
        // also feels alive when the user lands back on it.
        if (returningDetails) {
          animateScreenItems(returningDetails, ENTER_KEYFRAMES, ENTER_TIMING);
        }
      },
      { subtree: false },
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 600);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

const EXIT_KEYFRAMES = [
  { opacity: 1, transform: 'translateX(0)' },
  { opacity: 0, transform: 'translateX(1rem)' },
];

const EXIT_TIMING = {
  duration: 400,
  staggerMs: 50,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** @type {FillMode} */
  fill: 'forwards',
};

const ENTER_KEYFRAMES = [{ opacity: 0 }, { opacity: 1 }];

const ENTER_TIMING = {
  duration: 450,
  staggerMs: 60,
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** @type {FillMode} */
  fill: 'backwards',
};

/**
 * Collect the animated elements at the current "screen" depth of a details — the
 * row anchors/summaries visible to the user on that screen, plus screen-level
 * extras (mega-extras, utility-links, featured-content). Nested deeper details are
 * skipped because their items belong to a different screen.
 *
 * @param {HTMLDetailsElement} details
 * @returns {Element[]}
 */
function getScreenItems(details) {
  const isTopLevel = details.classList.contains('menu-drawer-container');
  const panel = isTopLevel
    ? details.querySelector(':scope > .menu-drawer')
    : details.querySelector(':scope > .menu-drawer__submenu');

  if (!panel) return [];

  const listContainer = isTopLevel
    ? panel.querySelector(':scope > .menu-drawer__navigation > ul')
    : panel.querySelector(':scope > .menu-drawer__inner-submenu > ul');

  /** @type {Element[]} */
  const items = [];

  listContainer?.querySelectorAll(':scope > .menu-drawer__list-item').forEach((li) => {
    const row = li.querySelector(
      ':scope > .menu-drawer__animated-element, :scope > details > .menu-drawer__animated-element',
    );
    if (row) items.push(row);
  });

  if (isTopLevel) {
    panel
      .querySelectorAll(':scope > .menu-drawer__utility-links, :scope > .menu-drawer__featured-content')
      .forEach((el) => items.push(el));
  } else {
    panel
      .querySelectorAll(':scope > .menu-drawer__inner-submenu > .menu-drawer__animated-element')
      .forEach((el) => items.push(el));
  }

  return items;
}

/**
 * Apply a keyframe's `opacity` / `transform` as inline styles. Used to lock items
 * into the animation's start state synchronously — so the next paint shows them
 * already at that state, avoiding the FOUC where they briefly render at their
 * natural style before the Web Animations engine applies the fill mode.
 *
 * @param {Element} item
 * @param {Keyframe} keyframe
 * @returns {{ hasOpacity: boolean, hasTransform: boolean }}
 */
function applyKeyframeInline(item, keyframe) {
  const hasOpacity = 'opacity' in keyframe;
  const hasTransform = 'transform' in keyframe;
  if (!(item instanceof HTMLElement)) return { hasOpacity, hasTransform };
  if (hasOpacity) item.style.opacity = String(keyframe.opacity);
  if (hasTransform) item.style.transform = String(keyframe.transform);
  return { hasOpacity, hasTransform };
}

/**
 * Lock the returning screen's items into the entrance start state before the
 * closing panel transitions out — otherwise they flash visible behind the
 * fading panel before Phase 3's animation kicks in. Inline styles are released
 * later by the `.finished` callback inside `animateScreenItems`.
 *
 * @param {HTMLDetailsElement} details
 */
function prehideItemsForReturn(details) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const startKeyframe = ENTER_KEYFRAMES[0] ?? {};
  for (const item of getScreenItems(details)) {
    applyKeyframeInline(item, startKeyframe);
  }
}

/**
 * Run the same keyframes on each screen item with a per-item stagger delay.
 * Returns the animation objects (so the caller can cancel them later) plus a
 * promise that resolves after every animation finishes.
 *
 * @param {HTMLDetailsElement} details
 * @param {Keyframe[]} keyframes
 * @param {{ duration: number, staggerMs: number, easing: string, fill: FillMode }} timing
 * @returns {{ animations: Animation[], finished: Promise<unknown> }}
 */
function animateScreenItems(details, keyframes, timing) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return { animations: [], finished: Promise.resolve() };
  }

  const items = getScreenItems(details);
  if (items.length === 0) {
    return { animations: [], finished: Promise.resolve() };
  }

  const startKeyframe = keyframes[0] ?? {};

  const animations = items.map((item, i) => {
    const { hasOpacity, hasTransform } = applyKeyframeInline(item, startKeyframe);

    const anim = item.animate(keyframes, {
      duration: timing.duration,
      delay: i * timing.staggerMs,
      easing: timing.easing,
      fill: timing.fill,
    });

    anim.finished
      .then(() => {
        if (!(item instanceof HTMLElement)) return;
        if (hasOpacity) item.style.removeProperty('opacity');
        if (hasTransform) item.style.removeProperty('transform');
      })
      .catch(() => {});

    return anim;
  });

  return {
    animations,
    finished: Promise.allSettled(animations.map((a) => a.finished)),
  };
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
